import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, AlertTriangle } from "lucide-react";

export default function CommunityGuidelinesPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-3xl" style={{ fontFamily: 'Space Grotesk' }}>
              Community Guidelines
            </CardTitle>
            <p className="text-sm text-gray-600">Building a respectful and supportive community</p>
          </CardHeader>
          <CardContent className="space-y-6 text-gray-700">
            <div className="bg-orange-50 border-l-4 border-orange-500 p-4 flex gap-3">
              <AlertTriangle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-orange-900">Zero Tolerance Policy</p>
                <p className="text-sm text-orange-800 mt-1">We have zero tolerance for harassment, discrimination, or inappropriate behavior. Violations will result in immediate account suspension or termination.</p>
              </div>
            </div>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>Our Commitment</h2>
              <p>NextGen Trades Academy is committed to providing a safe, inclusive, and respectful environment for all students, graduates, and community members. These guidelines help ensure our platform remains a positive space for professional networking and growth.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>Expected Behavior</h2>
              <ul className="space-y-2">
                <li className="flex gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Be Respectful:</strong> Treat everyone with dignity and respect, regardless of background, identity, or experience level</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Be Professional:</strong> Maintain professionalism in all communications and interactions</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Be Supportive:</strong> Encourage and support fellow community members in their career journey</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Be Honest:</strong> Provide accurate information in your profile and communications</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span><strong>Be Constructive:</strong> Offer helpful feedback and advice when appropriate</span>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>Prohibited Behavior</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-red-700 mb-2">Harassment and Discrimination</h3>
                  <ul className="list-disc ml-6 space-y-1">
                    <li>Sexual harassment, unwanted advances, or inappropriate comments</li>
                    <li>Discrimination based on race, ethnicity, gender, sexual orientation, religion, disability, age, or any protected characteristic</li>
                    <li>Bullying, threats, or intimidation</li>
                    <li>Stalking or repeated unwanted contact</li>
                    <li>Hate speech or slurs</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-red-700 mb-2">Inappropriate Content</h3>
                  <ul className="list-disc ml-6 space-y-1">
                    <li>Sexually explicit content or messages</li>
                    <li>Violent, graphic, or disturbing content</li>
                    <li>Illegal activities or content</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-red-700 mb-2">Deceptive Practices</h3>
                  <ul className="list-disc ml-6 space-y-1">
                    <li>False or misleading profile information</li>
                    <li>Impersonating others</li>
                    <li>Fraudulent job postings or scams</li>
                    <li>Phishing or attempts to obtain personal information</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-red-700 mb-2">Spam and Abuse</h3>
                  <ul className="list-disc ml-6 space-y-1">
                    <li>Unsolicited commercial messages or advertisements</li>
                    <li>Repetitive or excessive messaging</li>
                    <li>Posting irrelevant or off-topic content</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>Messaging Guidelines</h2>
              <ul className="list-disc ml-6 space-y-1">
                <li>Keep messages professional and respectful</li>
                <li>Respect boundaries if someone doesn't respond or declines to connect</li>
                <li>Do not send unsolicited sales pitches or promotional content</li>
                <li>Report inappropriate messages immediately using the Report feature</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>Reporting Violations</h2>
              <p className="mb-3">If you experience or witness behavior that violates these guidelines:</p>
              
              <div className="bg-teal-50 border-l-4 border-teal-500 p-4">
                <p className="font-semibold mb-2">How to Report:</p>
                <ul className="space-y-2">
                  <li>1. Use the "Report" button in messages or profiles</li>
                  <li>2. Email: <a href="mailto:compliance@ngta.com" className="text-teal-600 underline font-medium">compliance@ngta.com</a></li>
                  <li>3. Provide as much detail as possible (screenshots, dates, usernames)</li>
                </ul>
                <p className="mt-3 text-sm"><strong>All reports are confidential and will be investigated promptly.</strong></p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>Consequences</h2>
              <p>Violations of these guidelines may result in:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>Warning and required acknowledgment of guidelines</li>
                <li>Temporary suspension of account</li>
                <li>Permanent account termination</li>
                <li>Reporting to law enforcement if illegal activity is involved</li>
              </ul>
              <p className="mt-3">The severity of consequences depends on the nature and frequency of violations.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>California Legal Protections</h2>
              <p>Under California law (including Civil Code Section 1708.5), you are protected from harassment and discrimination. NextGen Trades Academy supports these protections and will cooperate with law enforcement when necessary.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>Questions?</h2>
              <p>If you have questions about these guidelines, contact us at:</p>
              <p className="mt-2">Email: <a href="mailto:support@ngta.com" className="text-teal-600 underline">support@ngta.com</a></p>
            </section>

            <div className="bg-gray-100 p-4 rounded-lg mt-6">
              <p className="text-sm text-center text-gray-700">
                <strong>Remember:</strong> Your safety and well-being are our priority. Don't hesitate to report concerns.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}